import { useEffect, useState } from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { Button } from "../../ui/button";
import { Calendar } from "../../ui/calendar";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { PrcDialogContent } from "./PrcDialogContent";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Textarea } from "../../ui/textarea";
import {
  findLiveListing,
  OWNERSHIP_VALUES,
  type InvestorListing,
  type InvestorListingProposalInput,
  type Ownership,
} from "../../utils/portfolio-risk-concentration/api/investorListing";

/**
 * Add, adjust or reactivate an investor code listing.
 *
 * Much smaller than AddCapitalSourceDialog because the record is: a code, a name, a note and
 * whether it is Owned or Serviced. There is no type fork and no per-fact planning — one LISTING
 * proposal carries the whole record, so the apply path has nothing to carry forward and no way to
 * get it wrong. The facility the code is mapped to is shown for context but is not part of the
 * record: it is read-only here and edited in Manage Capital.
 *
 * ── Three modes, one form ───────────────────────────────────────────────────────────────
 *   add          no live row for this code, code field editable
 *   adjust       a live row exists, code field locked (changing it would be a different code,
 *                not an edit of this one)
 *   reactivate   a superseded row exists and no live one. Identical to add, but seeded from
 *                the retired values so the common case — bring it back as it was — is one
 *                click. The backend derives change_type = 'ADD' from the absent incumbent.
 *
 * Retiring is NOT here: it is a different decision with nothing to fill in, so it has its own
 * confirm dialog (RetireInvestorCodeDialog) that collects only a reason.
 */
export interface InvestorCodeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Every listing the page has loaded, live and superseded — used to detect the mode. */
  listings: InvestorListing[];
  propose: (input: InvestorListingProposalInput) => Promise<unknown>;
  /** Seeds the form. A LIVE row means adjust; a SUPERSEDED one means reactivate. */
  editListing?: InvestorListing | null;
  onSubmitted?: (message: string) => void;
}

interface FormState {
  investorCode: string;
  codeMapping: string;
  notes: string;
  /** "" = not chosen yet. */
  ownership: Ownership | "";
  effectiveStartDate: Date;
  reason: string;
}

const EMPTY: FormState = {
  investorCode: "",
  codeMapping: "",
  notes: "",
  ownership: "",
  effectiveStartDate: new Date(),
  reason: "",
};

export function InvestorCodeDialog({
  open,
  onOpenChange,
  listings,
  propose,
  editListing,
  onSubmitted,
}: InvestorCodeDialogProps) {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset on open rather than on every render of `editListing`, so typing is never clobbered
  // by a background refetch handing back a new object with the same values.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setForm(
      editListing
        ? {
            investorCode: editListing.investorCode,
            codeMapping: editListing.codeMapping,
            notes: editListing.notes ?? "",
            ownership: editListing.ownership ?? "",
            effectiveStartDate: new Date(),
            reason: "",
          }
        : EMPTY,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editListing?.listingId]);

  // The mode is derived from what is LIVE right now, not from which button was clicked — the
  // same question the backend asks when it derives change_type, so the two cannot disagree.
  const live = form.investorCode ? findLiveListing(listings, form.investorCode.trim()) : undefined;
  const isAdjustment = !!live;
  const isReactivation = !live && !!editListing && editListing.status === "SUPERSEDED";

  async function submit() {
    setError(null);

    const code = form.investorCode.trim();
    if (!code) {
      setError("An investor code is required.");
      return;
    }
    if (!/^\d+$/.test(code)) {
      setError("An investor code is a whole number — for example 20.");
      return;
    }

    const mapping = form.codeMapping.trim();
    if (!mapping) {
      setError("A mapping name is required — for example 'NER 13-1'.");
      return;
    }

    if (!form.reason.trim()) {
      setError("A reason is required for every proposal.");
      return;
    }

    const notes = form.notes.trim();

    // A code being added or brought back must say which it is. An existing code that was never
    // given one may stay unset while its name or note is adjusted.
    if (!live && !form.ownership) {
      setError("Choose whether this code is Owned or Serviced.");
      return;
    }

    // Refused here as well as server-side: a no-op proposal would sit in the queue asking a
    // reviewer to approve nothing.
    if (
      live &&
      live.codeMapping === mapping &&
      (live.notes ?? "") === notes &&
      (live.ownership ?? "") === form.ownership
    ) {
      setError("Nothing has changed. Adjust the mapping, the notes or the ownership.");
      return;
    }

    setSubmitting(true);
    try {
      await propose({
        investorCode: code,
        changeKind: "LISTING",
        codeMapping: mapping,
        notes: notes || null,
        ownership: form.ownership || null,
        effectiveStartDate: format(form.effectiveStartDate, "yyyy-MM-dd"),
        reason: form.reason.trim(),
      });
      onOpenChange(false);
      onSubmitted?.(
        `${
          isAdjustment ? "Adjustment" : isReactivation ? "Reactivation" : "Listing"
        } proposed for investor code ${code} — awaiting review.`,
      );
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "The proposal could not be raised.");
    } finally {
      setSubmitting(false);
    }
  }

  const title = isAdjustment
    ? "Adjust investor code"
    : isReactivation
      ? "Reactivate investor code"
      : "Add investor code";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PrcDialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {isAdjustment ? (
              <>
                Code <strong>{live!.investorCode}</strong> is currently{" "}
                <strong>{live!.codeMapping}</strong>, effective {live!.effectiveStartDate}. The
                current row is closed out on approval so the history stays queryable.
              </>
            ) : isReactivation ? (
              <>
                Code <strong>{form.investorCode}</strong> is retired. Approving this brings it
                back as a new live listing; the retired version stays in the log.
              </>
            ) : (
              <>
                Nothing goes live until this is approved. This names a raw investor code so it
                reads as something other than a number wherever it appears.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="icl-code">Investor code</Label>
              <Input
                id="icl-code"
                inputMode="numeric"
                placeholder="20"
                // Locked while adjusting: a different code is a different listing, not an edit
                // of this one. Editable when reactivating, since that IS a creation.
                disabled={isAdjustment}
                value={form.investorCode}
                onChange={(e) => setForm((f) => ({ ...f, investorCode: e.target.value }))}
                className="mt-1 font-mono tabular-nums"
              />
            </div>

            <div>
              <Label>Effective from</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="mt-1 w-full justify-start gap-2 font-normal">
                    <CalendarIcon className="h-3.5 w-3.5" />
                    {format(form.effectiveStartDate, "d MMM yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={form.effectiveStartDate}
                    onSelect={(d) => d && setForm((f) => ({ ...f, effectiveStartDate: d }))}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div>
            <Label htmlFor="icl-mapping">Mapping</Label>
            <Input
              id="icl-mapping"
              placeholder="NER 13-1"
              value={form.codeMapping}
              onChange={(e) => setForm((f) => ({ ...f, codeMapping: e.target.value }))}
              className="mt-1"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              What this code is called — the name a reader recognises instead of the number.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Owned / Serviced</Label>
              <Select
                value={form.ownership || undefined}
                onValueChange={(v) => setForm((f) => ({ ...f, ownership: v as Ownership }))}
              >
                <SelectTrigger className="mt-1" aria-label="Owned or Serviced">
                  <SelectValue placeholder="Not set" />
                </SelectTrigger>
                <SelectContent>
                  {OWNERSHIP_VALUES.map((o) => (
                    <SelectItem key={o} value={o}>
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="icl-facility">Facility</Label>
              {/* Shown, never edited: the mapping lives in Manage Capital. For a code that is not
                  on the page yet there is nothing to show. */}
              <Input
                id="icl-facility"
                readOnly
                disabled
                value={editListing?.facility ?? live?.facility ?? "—"}
                className="mt-1 font-mono"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Set in Manage Capital, not here.
              </p>
            </div>
          </div>

          <div>
            <Label htmlFor="icl-notes">Notes</Label>
            <Textarea
              id="icl-notes"
              rows={2}
              placeholder="Term securitization; paid in full 2/17"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              Optional. Context a reader needs — what the vehicle was, and what became of it.
            </p>
          </div>

          <div>
            <Label htmlFor="icl-reason">Reason</Label>
            <Textarea
              id="icl-reason"
              rows={3}
              placeholder="Why is this listing being set or changed?"
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={submitting} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={submitting} onClick={submit}>
            {submitting
              ? "Submitting…"
              : isAdjustment
                ? "Propose adjustment"
                : isReactivation
                  ? "Propose reactivation"
                  : "Propose listing"}
          </Button>
        </DialogFooter>
      </PrcDialogContent>
    </Dialog>
  );
}

/**
 * Retire a code — end its live listing with no successor.
 *
 * Its own dialog rather than a mode of the one above because there is nothing to fill in: the
 * only thing a reviewer needs is why. Approving it leaves the code with no LIVE row, which is
 * what "inactive" means here.
 */
export function RetireInvestorCodeDialog({
  open,
  onOpenChange,
  listing,
  propose,
  onSubmitted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listing: InvestorListing | null;
  propose: (input: InvestorListingProposalInput) => Promise<unknown>;
  onSubmitted?: (message: string) => void;
}) {
  const [reason, setReason] = useState("");
  const [effective, setEffective] = useState<Date>(new Date());
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason("");
    setEffective(new Date());
    setError(null);
  }, [open]);

  async function submit() {
    if (!listing) return;
    if (!reason.trim()) {
      setError("A reason is required for every proposal.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await propose({
        investorCode: listing.investorCode,
        changeKind: "RETIRE",
        effectiveStartDate: format(effective, "yyyy-MM-dd"),
        reason: reason.trim(),
      });
      onOpenChange(false);
      onSubmitted?.(
        `Retirement proposed for investor code ${listing.investorCode} — awaiting review.`,
      );
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "The proposal could not be raised.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PrcDialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Retire investor code</DialogTitle>
          <DialogDescription>
            {listing ? (
              <>
                Approving this ends the live listing for code{" "}
                <strong>{listing.investorCode}</strong> ({listing.codeMapping}). Nothing is
                deleted — the row is closed out and stays in the history, and the code can be
                reactivated later.
                {listing.usedBy ? (
                  <>
                    {" "}
                    It is currently mapped to <strong>{listing.usedBy}</strong>; retiring the
                    listing does not change that mapping, only what the code is called.
                  </>
                ) : null}
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </div>
          )}

          <div>
            <Label>Effective from</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="mt-1 w-full justify-start gap-2 font-normal">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {format(effective, "d MMM yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-auto p-0">
                <Calendar mode="single" selected={effective} onSelect={(d) => d && setEffective(d)} />
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <Label htmlFor="icl-retire-reason">Reason</Label>
            <Textarea
              id="icl-retire-reason"
              rows={3}
              placeholder="Why is this code being retired?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={submitting} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" disabled={submitting} onClick={submit}>
            {submitting ? "Submitting…" : "Propose retirement"}
          </Button>
        </DialogFooter>
      </PrcDialogContent>
    </Dialog>
  );
}
